import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { withTranslation } from "react-i18next";
import constants from "../../../../../constants";
import CustomDropdown from "components/Dropdown";
import moment from "moment";
import { Portal } from "react-overlays";
import Text from "components/Text";
import Button from "components/Button";
import TimePicker from "rc-time-picker";
import styles from "../../AddEvent.module.scss";
import "../../AddEvent.scss";
import { Tooltip } from "reactstrap";
import SchedulingTable from "./SchedulingTable";
import StaffAvailabilityWeeklyView from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityView/StaffAvailabilityWeeklyView";
import WeeklyListView from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityView/WeeklyListView";
import DailyListView from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityView/DailyListView";
import { cacheSideBarActive } from "utils";
import {
  useGetOfficeAgendaMonthly,
  getAvailableDatesList,
} from "repositories/scheduler-repository";
import MonthlyViewNew from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityView/MonthlyViewNew";
import ExpandableSearchBar from "./ExpandableSearchBar";
import Empty from "components/Empty";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import FindAvailabilityIcon from "assets/images/Calendar-Find-Availability.svg";
import ArrowLeft from "assets/images/ArrowLeft.svg";
import ResetIcon from "assets/images/Reset.svg";
import ArrowRight from "assets/images/RightArrow.svg";
import { leaveClasses } from "accountOwner/pages/Scheduler/scheduler.function";
import { addEditEventsConst } from "../../addEvents.constants";
import RepeatEventDropDowns from "../../RepeatEventDropDowns";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { v4 as uuid4 } from "uuid";

const ASSISTANT_VIEW = {
  DAILY: "1",
  WEEKLY: "2",
  MONTHLY: "3",
  DAILY_LIST: "4",
  WEEKLY_LIST: "5",
};

const DAY_VIEWS = new Set([ASSISTANT_VIEW.DAILY, ASSISTANT_VIEW.DAILY_LIST]);
const WEEK_VIEWS = new Set([
  ASSISTANT_VIEW.WEEKLY,
  ASSISTANT_VIEW.WEEKLY_LIST,
]);

const useMinWidth = (minWidth) => {
  const [isMinWidth, setIsMinWidth] = useState(window.innerWidth >= minWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsMinWidth(window.innerWidth >= minWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [minWidth]);

  return isMinWidth;
};

/**
 * Resolves the next date for assistant navigation while enforcing "no past" behavior.
 */
const getAssistantNavigatedDate = ({ currentDate, viewId, direction }) => {
  const today = moment().startOf("day");
  const isPrevious = direction === constants.arrow.PREVIOUS;

  if (DAY_VIEWS.has(viewId)) {
    const nextDate = isPrevious
      ? moment(currentDate).subtract(1, "day")
      : moment(currentDate).add(1, "day");
    return nextDate.startOf("day").isSameOrAfter(today) ? nextDate.toDate() : null;
  }

  if (WEEK_VIEWS.has(viewId)) {
    const weekDate = isPrevious
      ? moment(currentDate).subtract(1, "week").startOf("isoweek")
      : moment(currentDate).add(1, "week").startOf("isoweek");
    const weekStart = weekDate.clone().startOf("isoweek");
    const weekEnd = weekDate.clone().endOf("isoweek");
    if (today.isSameOrAfter(weekStart) && today.isSameOrBefore(weekEnd)) {
      return today.toDate();
    }
    return weekDate.startOf("day").isSameOrAfter(today) ? weekDate.toDate() : null;
  }

  if (viewId === ASSISTANT_VIEW.MONTHLY) {
    const targetMonth = isPrevious
      ? moment(currentDate).subtract(1, "month").startOf("month")
      : moment(currentDate).add(1, "month").startOf("month");
    if (isPrevious && targetMonth.isSame(today, "month")) {
      return today.toDate();
    }
    if (targetMonth.isBefore(today, "month")) {
      return null;
    }
    return targetMonth.toDate();
  }

  return null;
};

const CalendarContainer = ({ children }) => {
  const el = document.getElementById("calendar-portal");

  return <Portal container={el}>{children}</Portal>;
};
const AssistantDetails = ({
  t,
  eventDate,
  setEventDate,
  startTime,
  endTime,
  endTimeError,
  handleDateTimeChange,
  selectedStaff,
  errors,
  agendaFilter,
  setAgendaFilter,
  settingFilter: settingFilterProps,
  assistantViewSelected,
  setAssistantViewSelected,
  schduleViews,
  agendaTypes,
  agendaFilteredData,
  setAgenda,
  showFreeSlots,
  setShowFreeSlots,
  eventOrShiftTab = false,
  manageTab,
  eventSchedulingTabStatus,
  showFindAvailabilityButtons,
  setShowFindAvailabilityButtons,
  checkFreeSlotAfterFilterApply,
  setCheckFreeSlotAfterFilterApply,
  options,
  selectedOption,
  selectOption,
  today,
  repeatMessagesDisplay,
  setRepeatTypeValues,
  disabledDates,
  prevSelectedValues,
  canShowOfficeName,
}) => {
  const { selectedId: selectedOwnerId, profile } = useAccountOwnerCommon();
  const currentUserId = profile.id;
  const [searchValue, setSearchValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [toolTips, setToolTips] = useState({
    findAvailability: null,
    createEventCreation: null,
    next: null,
    previous: null,
    resume: null,
  });
  const [tooltipReady, setTooltipReady] = useState(false);
  const [selectedHourIdx, setSelectedHourIdx] = useState(null); // Track selected hour for time label
  const [findAvailabilityData, setFindAvailabilityData] = useState(null); // Track selected hour for time label

  // Journey tracking state for Find Availability navigation
  const [journeyStartDate, setJourneyStartDate] = useState(null); // The date where journey started
  const [visitedDates, setVisitedDates] = useState([]); // Array of visited dates
  const [currentIndex, setCurrentIndex] = useState(-1); // Pointer to current position in journey
  const [firstAvailableDate, setFirstAvailableDate] = useState(null); // The first available date from API
  const [isFreeStotLoading, setIsFreeStotLoading] = useState(false);
  // repeat type states
  const [customViewShow, setCustomViewShow] = useState(false);
  const [repeatedTypeFrequency, setRepeatedTypeFrequency] = useState(
    prevSelectedValues?.repeatedTypeFrequency ?? 1,
  );
  const [repeatedTypeEvery, setRepeatedTypeEvery] = useState(
    prevSelectedValues?.repeatedTypeEvery ?? 1,
  );
  const [schedule, setSchedule] = useState(
    prevSelectedValues?.schedule ?? [today.getDay() === 0 ? 7 : today.getDay()],
  );
  const [selectionType, setSelectionType] = useState(
    prevSelectedValues?.selectionType ?? true,
  );
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const startOfMonth = moment(today).clone().startOf("month");
    return (
      prevSelectedValues?.selectedWeek ??
      moment(today).diff(startOfMonth, "weeks") + 1
    );
  });
  const [selectedDay, setSelectedDay] = useState(
    prevSelectedValues?.selectedDay > -1
      ? prevSelectedValues?.selectedDay
      : today.getDay() === 0
        ? 7
        : today.getDay(),
  );
  const [isDaySwitchOn, setIsDaySwitchOn] = useState(
    prevSelectedValues?.isDaySwitchOn ?? false,
  );
  const [selectedSlotDate, setSelectedSlotDate] = useState(
    prevSelectedValues?.selectedSlotDate ?? [today.getDate()],
  );
  const [selectedMonths, setSelectedMonths] = useState(
    prevSelectedValues?.selectedMonths ?? [today.getMonth() + 1],
  );
  useEffect(() => {
    if (!showFindAvailabilityButtons) {
      const timeout = setTimeout(() => {
        setTooltipReady(true);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      setTooltipReady(false);
    }
  }, [showFindAvailabilityButtons]);

  const isSidebarActive = useMemo(
    () => cacheSideBarActive(constants.sidebar.cache.schedulerIsSidebarActive),
    [],
  );
  const [dateRange, setDateRange] = useState(null);
  const enableFetch = selectedOwnerId && dateRange ? true : false;
  const {
    isLoading,
    data: monthlyData,
    error,
  } = useGetOfficeAgendaMonthly(
    selectedOwnerId,
    dateRange?.startDate,
    dateRange?.endDate,
    [],
    [],
    { enabled: enableFetch },
  );
  const { onlySelectedStaff, onlySelectedStaffOffice } = useMemo(
    () =>
      selectedStaff?.reduce(
        (prev, { userId, officeId }) => {
          if (!prev.mapping.has(userId)) {
            prev.onlySelectedStaff.push(userId);
            prev.mapping.set(userId);
          }
          if (!prev.mapping.has(officeId)) {
            prev.onlySelectedStaffOffice.push(officeId);
            prev.mapping.set(officeId);
          }
          return prev;
        },
        {
          onlySelectedStaff: [],
          onlySelectedStaffOffice: [],
          mapping: new Map(),
        },
      ),
    [selectedStaff],
  );

  useEffect(() => {
    if (!isLoading && monthlyData?.data) {
      setAgenda(monthlyData?.data);
      setAgendaFilter(
        monthlyData?.data.map((item) => {
          item.uniqueKey = uuid4();
          return item;
        }),
      );
    }
  }, [isLoading, monthlyData]);

  useEffect(() => {
    if (checkFreeSlotAfterFilterApply && showFreeSlots) {
      handleStartFindAvailability();
    }
  }, [checkFreeSlotAfterFilterApply]);

  const resetAvailabilityView = useCallback(() => {
    setShowFreeSlots(false);
    setShowFindAvailabilityButtons(false);
  }, [setShowFreeSlots, setShowFindAvailabilityButtons]);

  const dateChange = (type) => {
    resetAvailabilityView();
    const nextDate = getAssistantNavigatedDate({
      currentDate: eventDate,
      viewId: assistantViewSelected,
      direction: type,
    });
    if (nextDate) {
      setEventDate(nextDate);
    }
  };

  const isLargeScreen = useMinWidth(1200);

  const schedulingTableRef = useRef();
  const focusOnFirstAvailableSlot = useRef(false);
  const createAvailabilityParams = (startDate) => ({
    OwnerId: selectedOwnerId,
    StartDate: startDate.format("YYYY-MM-DD"),
    EndDate: startDate.clone().add(31, "days").format("YYYY-MM-DD"),
    UserIds: onlySelectedStaff,
    RequestedOffices: onlySelectedStaffOffice,
  });

  const applyAvailabilityResponse = (response) => {
    const nextDate = response?.data?.startTime ? new Date(response.data.startTime) : null;
    if (response?.data) {
      setFindAvailabilityData(response.data);
    }
    return nextDate;
  };

  // Handler for Find Availability button (default icon click)
  const handleStartFindAvailability = async () => {
    focusOnFirstAvailableSlot.current = true;
    try {
      setIsFreeStotLoading(true);
      setShowFindAvailabilityButtons(true);
      setJourneyStartDate(eventDate);

      let startDate = moment(eventDate).startOf("day");
      const response = await getAvailableDatesList(
        createAvailabilityParams(startDate),
      );
      const nextDate = applyAvailabilityResponse(response);
      let apiDate = nextDate ? moment(nextDate).startOf("day") : null;
      if (apiDate) {
        if (apiDate.isSame(startDate, "day")) {
          setVisitedDates([eventDate]);
          setCurrentIndex(0);
          setFirstAvailableDate(eventDate);
          setShowFreeSlots(true);
          // Current date is available, no need to change date
          return;
        } else {
          setEventDate(nextDate);
          setVisitedDates([nextDate]);
          setCurrentIndex(0);
          setFirstAvailableDate(nextDate);
          setShowFreeSlots(true);
        }
      } else {
        setVisitedDates([]);
        setCurrentIndex(-1);
        setFirstAvailableDate(null);
      }
    } catch (error) {
      console.error(error);
      focusOnFirstAvailableSlot.current = false;
    } finally {
      setIsFreeStotLoading(false);
      setCheckFreeSlotAfterFilterApply(false);
    }
  };

  // Handler for Next button
  const handleNextAvailability = async () => {
    focusOnFirstAvailableSlot.current = true;
    try {
      setIsFreeStotLoading(true);
      let baseDate = visitedDates[currentIndex];

      let startDate = moment(baseDate).startOf("day").add(1, "day");
      const response = await getAvailableDatesList(
        createAvailabilityParams(startDate),
      );
      const nextDate = applyAvailabilityResponse(response);
      if (nextDate) {
        const truncatedVisited = visitedDates.slice(0, currentIndex + 1);
        if (
          !moment(truncatedVisited[truncatedVisited.length - 1]).isSame(
            nextDate,
            "day",
          )
        ) {
          const newVisited = [...truncatedVisited, nextDate];
          setVisitedDates(newVisited);
          setCurrentIndex(newVisited.length - 1);
          setEventDate(nextDate);
          setShowFreeSlots(true);
        } else {
          setEventDate(nextDate);
          setShowFreeSlots(true);
        }
      }
    } catch (error) {
      console.error(error);
      focusOnFirstAvailableSlot.current = false;
    } finally {
      setIsFreeStotLoading(false);
    }
  };

  // Handler for Previous button
  const handlePreviousAvailability = () => {
    focusOnFirstAvailableSlot.current = true;
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setEventDate(visitedDates[currentIndex - 1]);
    }
  };

  // Handler for Resume button (now called from Reset)
  const handleResumeAvailability = () => {
    if (firstAvailableDate) {
      focusOnFirstAvailableSlot.current = true;
      // Find the index of firstAvailableDate in visitedDates, or fallback to 0
      const idx = visitedDates.findIndex((d) =>
        moment(d).isSame(firstAvailableDate, "day"),
      );
      if (idx !== -1) {
        setCurrentIndex(idx);
        setEventDate(firstAvailableDate);
      } else if (visitedDates.length > 0) {
        setCurrentIndex(0);
        setEventDate(visitedDates[0]);
      }
    }
  };
  const resetFocusRef = useCallback(() => {
    focusOnFirstAvailableSlot.current = false;
  }, []);
  // Add useEffect to call refetch after eventDate changes
  const prevEventDate = useRef(eventDate);
  useEffect(() => {
    if (
      prevEventDate.current !== eventDate &&
      schedulingTableRef.current?.refetch
    ) {
      schedulingTableRef.current.refetch();
    }
    prevEventDate.current = eventDate;
  }, [eventDate]);

  const handleContinueEventShiftCreation = () => {
    manageTab(eventSchedulingTabStatus.event);
  };

  const handleTimeLabelClick = (hourIdx) => {
    // Set start and end time for the selected hour
    const start = moment(eventDate).startOf("day").add(hourIdx, "hours");
    const end = moment(eventDate)
      .startOf("day")
      .add(hourIdx + 1, "hours");
    handleDateTimeChange("startTime", start);
    handleDateTimeChange("endTime", end);
    setSelectedHourIdx(hourIdx);
  };

  // When a staff cell is clicked (onFreeSlotClick), clear the time label highlight
  const handleFreeSlotClick = (slotStart, slotEnd) => {
    handleDateTimeChange("startTime", moment(slotStart));
    handleDateTimeChange("endTime", moment(slotEnd));
    setSelectedHourIdx(null);
  };

  const renderSelectedTable = () => {
    switch (assistantViewSelected) {
      case "1":
        return (
          <div className={styles.daily_view_table_scroll + " " + "table-fix"}>
            <SchedulingTable
              ref={schedulingTableRef}
              selectedStaff={selectedStaff}
              selectedOwnerId={selectedOwnerId}
              currentUserId={currentUserId}
              eventDate={eventDate}
              viewSettingFilter={settingFilterProps}
              agendaTypes={agendaTypes}
              cachedEventFilter={agendaFilteredData}
              searchValue={searchValue}
              showFreeSlots={showFreeSlots}
              startTime={startTime}
              endTime={endTime}
              onFreeSlotClick={handleFreeSlotClick}
              onTimeLabelClick={handleTimeLabelClick}
              selectedHourIdx={selectedHourIdx}
              disabledDates={disabledDates}
              isFreeStotLoading={isFreeStotLoading}
              canShowOfficeName={canShowOfficeName}
            />
          </div>
        );
      case "2":
        return (
          <StaffAvailabilityWeeklyView
            currentDate={eventDate}
            selectedOwnerId={selectedOwnerId}
            currentUserId={currentUserId}
            onDateChange={(date) => {
              resetAvailabilityView();
              setEventDate(date);
            }}
            isSidebarActive={isSidebarActive}
            isWeekly={assistantViewSelected === "2"}
            agendaTypes={agendaTypes}
            cachedEventFilter={agendaFilteredData}
            viewSettingFilter={settingFilterProps}
            context="scheduling-assistant"
            selectedStaff={selectedStaff}
            selectedOption={selectedOption}
            disabledDates={disabledDates}
            searchValue={searchValue}
            showFreeSlots={showFreeSlots}
            isFreeStotLoading={isFreeStotLoading}
            canShowOfficeName={canShowOfficeName}
          />
        );
      case "3":
        return (
          <MonthlyViewNew
            currentDate={eventDate}
            selectedOwnerId={selectedOwnerId}
            currentUserId={currentUserId}
            onDateChange={(date) => {
              resetAvailabilityView();
              setEventDate(date);
            }}
            setDateRange={setDateRange}
            isLoading={isLoading || isFreeStotLoading}
            agenda={agendaFilter}
            viewSettingFilter={settingFilterProps}
            error={error}
            context="scheduling-assistant"
            selectedStaff={selectedStaff}
            searchValue={searchValue}
            isFromDashBoard={false}
            agendaTypes={agendaFilteredData}
            cachedEventFilter={agendaFilteredData || []}
            showFreeSlots={showFreeSlots}
            selectedOption={selectedOption}
            disabledDates={disabledDates}
            canShowOfficeName={canShowOfficeName}
          />
        );
      case "4":
        return (
          <DailyListView
            currentDate={eventDate}
            selectedOwnerId={selectedOwnerId}
            currentUserId={currentUserId}
            apiOffices={[]}
            selectedStaff={selectedStaff}
            apiEmps={onlySelectedStaff}
            context="scheduling-assistant"
            agendaTypes={agendaTypes}
            cachedEventFilter={agendaFilteredData || []}
            viewSettingFilter={settingFilterProps}
            searchValue={searchValue}
            isEnterpriseFilterActive={false}
            isFreeStotLoading={isLoading || isFreeStotLoading}
            canShowOfficeName={canShowOfficeName}
            showFreeSlots={showFreeSlots}
            startTime={startTime}
            onFreeSlotClick={handleFreeSlotClick}
            onTimeLabelClick={handleTimeLabelClick}
            selectedHourIdx={selectedHourIdx}
            resetFocusRef={resetFocusRef}
            focusOnFirstAvailableSlot={focusOnFirstAvailableSlot}
            onDateChange={(date) => {
              resetAvailabilityView();
              setEventDate(date);
            }}
            findAvailabilityData={findAvailabilityData}
          />
        );
      case "5":
        return (
          <WeeklyListView
            currentDate={eventDate}
            selectedOwnerId={selectedOwnerId}
            currentUserId={currentUserId}
            apiOffices={[]}
            selectedStaff={selectedStaff}
            apiEmps={onlySelectedStaff}
            context="scheduling-assistant"
            agendaTypes={agendaTypes}
            cachedEventFilter={agendaFilteredData || []}
            viewSettingFilter={settingFilterProps}
            searchValue={searchValue}
            findAvailabilityData={findAvailabilityData}
            isEnterpriseFilterActive={false}
            isFreeStotLoading={isLoading || isFreeStotLoading}
            canShowOfficeName={canShowOfficeName}
            onTimeLabelClick={handleTimeLabelClick}
            onFreeSlotClick={handleFreeSlotClick}
            selectedHourIdx={selectedHourIdx}
            startTime={startTime}
            showFreeSlots={showFreeSlots}
            resetFocusRef={resetFocusRef}
            focusOnFirstAvailableSlot={focusOnFirstAvailableSlot}
            onDateChange={(date) => {
              resetAvailabilityView();
              setEventDate(date);
            }}
          />
        );
      default:
        return null;
    }
  };
  return (
    <>
      <div
        className={styles.tabs_right_col + " tabs-right-col"}
        id="targetElementId"
      >
        <div className={styles["staff-availability-header"]}>
          <ul className={styles["view-color-list"]}>
            <li>
              <div className={styles["color-box"]} />
              <div className={styles["text-box"]}>
                {" "}
                {t("accountOwner.createdByYou")}{" "}
              </div>
            </li>
            <li>
              <div
                className={`${styles["color-box"]} ${styles["color-grey"]}`}
              />
              <div className={styles["text-box"]}>
                {" "}
                {t("accountOwner.createdByOthers")}{" "}
              </div>
            </li>
            <li>
              <div
                className={`${styles["color-box"]} ${styles["color-blue"]}`}
              />
              <div className={styles["text-box"]}>
                {" "}
                {t("accountOwner.eventsNotAccepted")}{" "}
              </div>
            </li>
            <li>
              <div
                className={`${styles["color-box"]} ${styles["color-orange"]}`}
              />
              <div className={styles["text-box"]}>
                {" "}
                {t("accountOwner.busy")}{" "}
              </div>
            </li>
            <li>
              <div
                className={
                  styles["color-box"] +
                  " " +
                  styles[leaveClasses(constants.agendaType.PENDINGLEAVE).main]
                }
              />
              <div className={styles["text-box"]}>
                {" "}
                {t("scheduler.agenda.pendingLeave")}{" "}
              </div>
            </li>
            <li>
              <div
                className={
                  styles["color-box"] +
                  " " +
                  styles[leaveClasses(constants.agendaType.APPROVEDLEAVE).main]
                }
              />
              <div className={styles["text-box"]}>
                {" "}
                {t("scheduler.agenda.approvedLeave")}{" "}
              </div>
            </li>
            <li>
              <div
                className={`${styles["color-box"]} ${styles["light-purple-bg"]}`}
              />
              <div className={styles["text-box"]}> {t("appointments")} </div>
            </li>
            <li>
              <div
                className={`${styles["color-box"]} ${styles["light-green-bg"]}`}
              />
              <div className={styles["text-box"]}> {t("availableSlots")} </div>
            </li>
          </ul>
          <div className="d-flex staff-calendar-select-box">
            <div className="d-flex staff-calendar-select-box">
              <div className="monthly-calendar-arrows">
                <>
                  <div
                    className="arrow-img"
                    onClick={() => dateChange(constants.arrow.PREVIOUS)}
                  >
                    <img
                      src={
                        require("assets/images/monthly-arrow-left.svg").default
                      }
                      alt="icon"
                    />
                  </div>

                  <div className="monthly-calendar-text">
                    {(assistantViewSelected === "1" ||
                      assistantViewSelected === "4") &&
                      moment(eventDate).format("ddd, MMM DD")}
                    {assistantViewSelected === "3" &&
                      moment(eventDate).format("MMM YYYY")}

                    {(assistantViewSelected === "2" ||
                      assistantViewSelected === "5") && (
                      <>
                        {`${moment(eventDate)
                          .startOf("isoweek")
                          .format("ddd, MMM DD")} - ${moment(eventDate)
                          .endOf("isoweek")
                          .format("ddd, MMM DD")} `}
                      </>
                    )}
                  </div>
                  <div
                    className="arrow-img"
                    onClick={() => dateChange(constants.arrow.NEXT)}
                  >
                    <img
                      src={
                        require("assets/images/monthly-arrow-right.svg").default
                      }
                      alt="icon"
                    />
                  </div>
                </>
              </div>
            </div>

            <CustomDropdown
              options={schduleViews}
              selectedOption={assistantViewSelected}
              selectOption={setAssistantViewSelected}
            />
          </div>
        </div>
      </div>
      <div className={styles.scheduling_box_header}>
        <ExpandableSearchBar
          placeholder={t("accountOwner.searchForTitleAndStaff")}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
        />
        {(isLargeScreen ? !isFocused : true) && (
          <div className={styles.date_tooltip_btn}>
            <div className={styles["date_wrapper_assistant"] + " c-field mb-0"}>
              <div className={styles.common_width + " new_inputdate inputdate"}>
                <Text size="13px" weight="400" color="#79869A">
                  Start Date
                </Text>
                <ReactDatePickerCommon
                  dateFormat="dd-MM-yyyy"
                  className="c-form-control"
                  selected={eventDate}
                  onChange={(e) => {
                    resetAvailabilityView();
                    handleDateTimeChange("eventDate", e);
                  }}
                  minDate={new Date()}
                  popperPlacement="bottom-start"
                  popperContainer={CalendarContainer}
                  todayButton="Today"
                  showMonthDropdown
                  showYearDropdown
                />
              </div>
              <div className={styles.common_width + " time_picker c-field"}>
                <label>{t("start")}</label>
                <TimePicker
                  showSecond={false}
                  placeholder="--"
                  format="h:mm A"
                  use12Hours
                  onChange={(e) => handleDateTimeChange("startTime", e)}
                  className="event-time-picker"
                  value={startTime}
                  minuteStep={1}
                />
                {errors?.startTime && (
                  <span className="error-msg">{errors.startTime}</span>
                )}
              </div>
              <div className={styles.common_width + " time_picker c-field"}>
                <label>{t("end")}</label>
                <TimePicker
                  showSecond={false}
                  placeholder="--"
                  format="h:mm A"
                  use12Hours
                  onChange={(e) => handleDateTimeChange("endTime", e)}
                  className="event-time-picker"
                  value={endTime}
                  minuteStep={1}
                />
                {errors?.endTime && (
                  <span className="error-msg">{errors.endTime}</span>
                )}
                {endTimeError && (
                  <span className="error-msg">{endTimeError}</span>
                )}
              </div>
              <div
                className={
                  styles.common_width +
                  " c-field" +
                  (customViewShow ? " position-relative" : "")
                }
              >
                <label>{t("repeat")}</label>
                <div className="custom-dropdown-only">
                  <CustomDropdown
                    options={options}
                    selectedOption={selectedOption}
                    selectOption={(id) => {
                      if (
                        id === addEditEventsConst.repeatedOptionsValues.custom
                      ) {
                        setCustomViewShow(true);
                      }
                      selectOption(id);
                    }}
                    onChanged={(openOrClosed) => {
                      if (openOrClosed && customViewShow) {
                        setCustomViewShow(false);
                      }
                    }}
                  />
                </div>
                {customViewShow && (
                  <div className={styles["repeat-event-float"]}>
                    <RepeatEventDropDowns
                      selectionType={selectionType}
                      selectedSlotDate={selectedSlotDate}
                      selectedWeek={selectedWeek}
                      selectedDay={selectedDay}
                      selectedMonths={selectedMonths}
                      isDaySwitchOn={isDaySwitchOn}
                      repeatedTypeEvery={repeatedTypeEvery}
                      repeatedTypeFrequency={repeatedTypeFrequency}
                      schedule={schedule}
                      eventOrShiftTab={eventOrShiftTab}
                      setSchedule={setSchedule}
                      setSelectedMonths={setSelectedMonths}
                      setSelectedSlotDate={setSelectedSlotDate}
                      setRepeatedTypeEvery={setRepeatedTypeEvery}
                      setRepeatedTypeFrequency={setRepeatedTypeFrequency}
                      setSelectionType={setSelectionType}
                      setSelectedWeek={setSelectedWeek}
                      setSelectedDay={setSelectedDay}
                      setIsDaySwitchOn={setIsDaySwitchOn}
                      hideMessage
                    />
                    <div className={"add-event-btn-box-new "}>
                      <button
                        className="button button-round button-shadow text-capitalize"
                        onClick={() => {
                          setRepeatTypeValues({
                            selectionType: selectionType,
                            selectedSlotDate: selectedSlotDate,
                            selectedWeek: selectedWeek,
                            selectedDay: selectedDay,
                            selectedMonths: selectedMonths,
                            isDaySwitchOn: isDaySwitchOn,
                            repeatedTypeEvery: repeatedTypeEvery,
                            repeatedTypeFrequency: repeatedTypeFrequency,
                            schedule: schedule,
                          });
                          setCustomViewShow(false);
                        }}
                      >
                        {t("save")}
                      </button>
                      <button
                        className="button button-round button-dark button-border"
                        title={t("cancel")}
                        onClick={() => {
                          setCustomViewShow(false);
                        }}
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <Button
          whiteSpace="nowrap"
          onClick={handleContinueEventShiftCreation}
          height="53px"
          id="continue"
          onMouseOver={() => setToolTips({ createEventCreation: "continue" })}
          onMouseLeave={() => setToolTips({ createEventCreation: null })}
        >
          {t("scheduler.continueEventCreation", {
            eventOrShift: eventOrShiftTab ? "Shift" : "Event",
          })}
        </Button>
        {toolTips?.createEventCreation === "continue" && (
          <Tooltip
            className={styles["new-item-card-catalogue-tooltip"]}
            isOpen
            placement="top"
            target="continue"
          >
            <Text size="10px" weight="700" color="#ffffff">
              {t("continueEvent", {
                eventOrShift: eventOrShiftTab ? "Shift" : "Event",
              })}
            </Text>
          </Tooltip>
        )}
        {selectedStaff?.length > 0 && (
          <>
            {!showFindAvailabilityButtons ? (
              <>
                <Button
                  whiteSpace="nowrap"
                  onClick={handleStartFindAvailability}
                  padding="0 25px"
                  id="find_avail"
                  onMouseOver={() =>
                    setToolTips({ findAvailability: "find_avail" })
                  }
                  onMouseLeave={() => setToolTips({ findAvailability: null })}
                  height="53px"
                >
                  <img
                    src={FindAvailabilityIcon}
                    alt="Find Availability"
                    className={styles.find_availability_icon}
                  />
                </Button>

                {tooltipReady &&
                  toolTips?.findAvailability === "find_avail" && (
                    <Tooltip
                      className={styles["new-item-card-catalogue-tooltip"]}
                      isOpen
                      placement="top"
                      target="find_avail"
                    >
                      <Text size="10px" weight="700" color="#ffffff">
                        Find Availability
                      </Text>
                    </Tooltip>
                  )}
              </>
            ) : (
              <div className={styles.availability_toggle_group}>
                <Button
                  onClick={handlePreviousAvailability}
                  padding="0 23px"
                  border="none"
                  disabled={currentIndex === 0}
                  className={currentIndex === 0 ? styles.prev_back_btn : ""}
                  id="previous_avail"
                  onMouseOver={() =>
                    setToolTips({ previous: "previous_avail" })
                  }
                  onMouseLeave={() => setToolTips({ previous: null })}
                >
                  <img
                    src={ArrowLeft}
                    alt="Find Availability"
                    className={styles.find_availability_icon}
                  />
                </Button>
                {toolTips?.previous === "previous_avail" && (
                  <Tooltip
                    className={styles["new-item-card-catalogue-tooltip"]}
                    isOpen
                    placement="top"
                    target="previous_avail"
                  >
                    <Text size="10px" weight="700" color="#ffffff">
                      {t("previousAvailableSlot")}
                    </Text>
                  </Tooltip>
                )}
                <Button
                  onClick={() => {
                    setShowFindAvailabilityButtons(false);
                    setJourneyStartDate(null);
                    setVisitedDates([]);
                    setCurrentIndex(-1);
                    handleResumeAvailability();
                  }}
                  padding="0 27px"
                  border="none"
                  id="resume_avail"
                  onMouseOver={() => setToolTips({ resume: "resume_avail" })}
                  onMouseLeave={() => setToolTips({ resume: null })}
                >
                  <img
                    src={ResetIcon}
                    alt="Find Availability"
                    className={styles.find_availability_icon}
                  />
                </Button>
                {toolTips?.resume === "resume_avail" && (
                  <Tooltip
                    className={styles["new-item-card-catalogue-tooltip"]}
                    isOpen
                    placement="top"
                    target="resume_avail"
                  >
                    <Text size="10px" weight="700" color="#ffffff">
                      {t("resumeAvailableSlot")}
                    </Text>
                  </Tooltip>
                )}
                <Button
                  onClick={handleNextAvailability}
                  padding="0 23px"
                  border="none"
                  id="next_avail"
                  onMouseOver={() => setToolTips({ next: "next_avail" })}
                  onMouseLeave={() => setToolTips({ next: null })}
                >
                  <img
                    src={ArrowRight}
                    alt="Find Availability"
                    className={styles.find_availability_icon}
                  />
                </Button>
                {toolTips?.next === "next_avail" && (
                  <Tooltip
                    className={styles["new-item-card-catalogue-tooltip"]}
                    isOpen
                    placement="top"
                    target="next_avail"
                  >
                    <Text size="10px" weight="700" color="#ffffff">
                      {t("nextAvailableSlot")}
                    </Text>
                  </Tooltip>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {repeatMessagesDisplay && (
        <Text size="11px" weight="400" color="#79869A" className="mt-3">
          {repeatMessagesDisplay}{" "}
          <Text
            display="inline"
            className="link-btn-with-out-font ml-1"
            color="#8cae2b"
            size="12px"
            onClick={() => {
              setCustomViewShow(!customViewShow);
            }}
          >
            {t("change")}
          </Text>
        </Text>
      )}
      <div className={`margin30   ${styles["table-fix-wrapper"]}`}>
        {selectedStaff?.length > 0 ? (
          renderSelectedTable()
        ) : (
          <Empty Message={t("schedulingEmptyMsg")} />
        )}
      </div>
    </>
  );
};

export default withTranslation()(AssistantDetails);
